import React, { useMemo, useRef, useState } from 'react';
import { Box, Popover } from '@mui/material';
import mappedIcon from 'assets/images/mappedicon.svg';
import { formatCurrency } from 'utils';

interface Calculation {
    name: string;
    value: number;
    operation?: 'add' | 'subtract';
    displayRole?: 'subtotal';
    isPrimary?: boolean;
    sectionLabel?: string;
    isLineItem?: boolean;
    hideDividerAfter?: boolean;
    differenceLabel?: string;
    positiveDifferenceLabel?: string;
    negativeDifferenceLabel?: string;
    hideOperationPrefix?: boolean;
}

interface TileCalculationPopoverProps {
    calculations?: Calculation[];
    showZeroDiscrepancyPopover?: boolean;
    children: React.ReactNode;
}

const TileCalculationPopover: React.FC<TileCalculationPopoverProps> = ({
    calculations,
    showZeroDiscrepancyPopover = false,
    children,
}) => {
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const wrapperRef = useRef<HTMLSpanElement | null>(null);

    const resolveAnchorElement = () => (
        wrapperRef.current?.querySelector('.audit-report-card') as HTMLElement | null
    );

    const { shouldShowPopover, total } = useMemo(() => {
        if (!calculations || calculations.length <= 1) {
            return { shouldShowPopover: false, total: 0 };
        }

        const calculatedTotal = calculations.reduce((sum, item) => {
            if (item.displayRole === 'subtotal') {
                return sum;
            }

            const value = Number(item.value) || 0;
            return item.operation === 'subtract' ? sum - value : sum + value;
        }, 0);

        const hasNonZeroCalculationValue = calculations.some((item) => Number(item.value) !== 0);
        const shouldShowZeroDiscrepancyState = calculatedTotal === 0 && showZeroDiscrepancyPopover && hasNonZeroCalculationValue;

        return {
            shouldShowPopover: calculatedTotal !== 0 || shouldShowZeroDiscrepancyState,
            total: calculatedTotal,
        };
    }, [calculations, showZeroDiscrepancyPopover]);
    const differenceLabel = calculations?.find((item) => item.differenceLabel)?.differenceLabel ?? 'Discrepancy';
    const positiveDifferenceLabel = calculations?.find((item) => item.positiveDifferenceLabel)?.positiveDifferenceLabel;
    const negativeDifferenceLabel = calculations?.find((item) => item.negativeDifferenceLabel)?.negativeDifferenceLabel;

    if (!shouldShowPopover || !calculations || calculations.length <= 1) {
        return <>{children}</>;
    }

    return (
        <>
            <Box
                component="span"
                ref={wrapperRef}
                sx={{ display: 'contents' }}
                onMouseOver={(event: React.MouseEvent<HTMLSpanElement>) => {
                    const target = event.target as HTMLElement | null;
                    if (target?.closest('.audit-report-card-extra')) {
                        setAnchorEl(null);
                        return;
                    }

                    setAnchorEl(resolveAnchorElement());
                }}
                onMouseOut={(event: React.MouseEvent<HTMLSpanElement>) => {
                    const nextTarget = event.relatedTarget as Node | null;

                    if (nextTarget && wrapperRef.current?.contains(nextTarget)) {
                        return;
                    }

                    setAnchorEl(null);
                }}
            >
                {children}
            </Box>

            <Popover
                open={Boolean(anchorEl)}
                anchorEl={anchorEl}
                anchorOrigin={{ vertical: 'center', horizontal: 'right' }}
                transformOrigin={{ vertical: 'center', horizontal: 'left' }}
                onClose={() => setAnchorEl(null)}
                disableRestoreFocus
                disableScrollLock
                sx={{
                    pointerEvents: 'none',
                    zIndex: (theme) => theme.zIndex.modal + 1,
                }}
            >
                <Box sx={{ p: 2, minWidth: '280px', backgroundColor: '#f5f5f5' }}>
                    <div style={{ fontFamily: 'monospace', fontSize: '14px' }}>
                        {calculations.map((item, index) => {
                            const isSubtotal = item.displayRole === 'subtotal';
                            const nextIsSubtotal = calculations[index + 1]?.displayRole === 'subtotal';
                            const isLineItem = !isSubtotal && (index !== 0 || item.isLineItem);
                            const operationPrefix = index === 0 || isSubtotal || item.hideOperationPrefix ? '' : (item.operation === 'subtract' ? '-' : '+');

                            return (
                                <React.Fragment key={`${item.name}-${index}`}>
                                    {item.sectionLabel && (
                                        <div
                                            style={{
                                                color: '#555',
                                                fontSize: '0.75em',
                                                fontWeight: 700,
                                                letterSpacing: '0.04em',
                                                marginTop: index === 0 ? 0 : '10px',
                                                marginBottom: '4px',
                                                textTransform: 'uppercase',
                                            }}
                                        >
                                            {item.sectionLabel}
                                        </div>
                                    )}
                                    <div
                                        style={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            gap: '16px',
                                            marginBottom: index < calculations.length - 1 ? '4px' : '8px',
                                            borderTop: isSubtotal ? '1px solid #999' : 'none',
                                            borderBottom: index < calculations.length - 1 && !isSubtotal && !nextIsSubtotal && !item.hideDividerAfter ? '1px solid #ddd' : 'none',
                                            paddingTop: isSubtotal ? '6px' : '0',
                                            paddingBottom: index < calculations.length - 1 ? '4px' : '0',
                                            fontWeight: isSubtotal ? 700 : undefined,
                                            fontSize: isSubtotal ? '0.95em' : undefined,
                                        }}
                                    >
                                        <span style={{ color: isSubtotal ? '#23243A' : '#666' }}>
                                            {!isLineItem ? (
                                                item.name
                                            ) : (
                                                <span style={{ fontSize: '0.85em' }}>
                                                    {item.hideOperationPrefix ? '' : `${item.operation === 'subtract' ? 'less' : 'add'} `}{item.name}
                                                </span>
                                            )}
                                        </span>
                                        <span style={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>
                                            {operationPrefix} {formatCurrency(item.value)}
                                        </span>
                                    </div>
                                </React.Fragment>
                            );
                        })}
                        <div
                            style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                gap: '16px',
                                borderTop: '2px solid #333',
                                paddingTop: '8px',
                                marginTop: '8px',
                                fontWeight: 'bold',
                                fontSize: '0.92em',
                            }}
                        >
                            <span>{differenceLabel}</span>
                            {total === 0 ? (
                                <img src={mappedIcon} alt="matched" style={{ width: '18px', height: '18px' }} />
                            ) : (
                                <span style={{ color: total >= 0 ? '#0000FF' : 'red', whiteSpace: 'nowrap' }}>
                                    {total >= 0 ? '+' : '-'}{formatCurrency(Math.abs(total))}
                                </span>
                            )}
                        </div>
                        {total !== 0 && (
                            <div style={{ fontSize: '0.75em', color: '#666', marginTop: '4px' }}>
                                {positiveDifferenceLabel && negativeDifferenceLabel ? (
                                    <span style={{ color: total >= 0 ? '#0000FF' : 'red' }}>
                                        {total >= 0 ? positiveDifferenceLabel : negativeDifferenceLabel} {total >= 0 ? '+' : '-'}{formatCurrency(Math.abs(total))}
                                    </span>
                                ) : total >= 0 ? (
                                    <span style={{ color: '#0000FF' }}>+ve is profit</span>
                                ) : (
                                    <span style={{ color: 'red' }}>-ve is loss</span>
                                )}
                            </div>
                        )}
                    </div>
                </Box>
            </Popover>
        </>
    );
};

export default TileCalculationPopover;
